import React, { useState, useEffect } from 'react';
import {
  X,
  MapPin,
  Clock,
  Calendar,
  AlertCircle,
  Sparkles,
  ShieldCheck,
  Send,
  CheckCircle2,
  FileText,
  DollarSign,
  Maximize2,
  ExternalLink,
  Phone,
  User,
  AlertTriangle,
  Layers,
  Wrench,
  HelpCircle,
  Video,
  Image as ImageIcon,
  MessageSquare,
  Camera,
  Star,
} from 'lucide-react';
import { Job } from '../../types';
import { useApp } from '../../context/AppContext';
import { CategoryIcon } from '../common/CategoryIcon';
import { ShareJobButton } from '../common/ShareJobButton';
import { SubmitQuoteModal } from '../../pages/professional/SubmitQuoteModal';
import { CompletionPhotoUploadModal } from '../professional/CompletionPhotoUploadModal';
import { JobRatingModal } from './JobRatingModal';
import { RealTimeJobStatusTracker } from './RealTimeJobStatusTracker';
import {
  fetchTemporaryCompletionPhoto,
  confirmJobWorkCompletionApi,
  releaseJobPaymentRpc,
  cancelJobApi,
  reportNoShowApi,
  fetchReassignmentCandidatesApi,
  reassignJobApi,
  markJobArrivedApi,
  reportJobDelayApi,
  rescheduleJobAppointmentApi,
  proposeJobArrivalEtaApi,
  confirmJobArrivalEtaApi,
  confirmProfessionalArrivalByCustomerApi,
  cleanContactPhone,
  ReassignmentCandidate,
} from '../../lib/supabaseService';

interface JobDetailsModalProps {
  job: Job | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenSubmitQuote?: (job: Job) => void;
}

// Modal-level Error Boundary to safeguard against malformed data
class ModalErrorBoundary extends React.Component<
  { children: React.ReactNode; onClose: () => void },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; onClose: () => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.warn('[JobDetailsModal] Render warning:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full text-center space-y-4 shadow-2xl">
            <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mx-auto text-xl font-bold">
              ⚠️
            </div>
            <h3 className="text-base font-bold text-slate-900">Job Details Unavailable</h3>
            <p className="text-xs text-slate-500">
              Some details for this job record could not be formatted. You can still manage this request from your dashboard.
            </p>
            <button
              onClick={this.props.onClose}
              className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export const JobDetailsModal: React.FC<JobDetailsModalProps> = ({
  job,
  isOpen,
  onClose,
  onOpenSubmitQuote,
}) => {
  if (!isOpen || !job) return null;

  return (
    <ModalErrorBoundary onClose={onClose}>
      <JobDetailsModalContent
        job={job}
        onClose={onClose}
        onOpenSubmitQuote={onOpenSubmitQuote}
      />
    </ModalErrorBoundary>
  );
};

const JobDetailsModalContent: React.FC<{
  job: Job;
  onClose: () => void;
  onOpenSubmitQuote?: (job: Job) => void;
}> = ({ job: initialJob, onClose, onOpenSubmitQuote }) => {
  const { currentUser, quotes, profiles, navigateTo, refreshJobsAndQuotes } = useApp();
  const [job, setJob] = useState<Job>(initialJob);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | null>(null);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
  const [isConfirmDoneDialogOpen, setIsConfirmDoneDialogOpen] = useState(false);
  const [isConfirmingCompletion, setIsConfirmingCompletion] = useState(false);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [completionFeedback, setCompletionFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [completionNotes, setCompletionNotes] = useState('');
  const [tempCompletionPhoto, setTempCompletionPhoto] = useState<string | null>(initialJob?.completionPhotoUrl || null);

  // Cancellation, No-Show & Reassignment Modal State
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelWorkStarted, setCancelWorkStarted] = useState(false);
  const [isCancellingJob, setIsCancellingJob] = useState(false);

  const [isNoShowModalOpen, setIsNoShowModalOpen] = useState(false);
  const [noShowReason, setNoShowReason] = useState('');
  const [isReportingNoShow, setIsReportingNoShow] = useState(false);

  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [reassignmentCandidates, setReassignmentCandidates] = useState<ReassignmentCandidate[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedReplacementProId, setSelectedReplacementProId] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [isReassigningJob, setIsReassigningJob] = useState(false);

  // Professional Arrival, Delay Reporting & Rescheduling State
  const [isMarkingArrived, setIsMarkingArrived] = useState(false);
  const [isDelayModalOpen, setIsDelayModalOpen] = useState(false);
  const [delayMinutes, setDelayMinutes] = useState(30);
  const [delayReason, setDelayReason] = useState('Heavy traffic in transit');
  const [isReportingDelay, setIsReportingDelay] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('Mutual schedule adjustment');
  const [isRescheduling, setIsRescheduling] = useState(false);

  // 60-Minute ETA Proposal, Confirmation & Customer Arrival Verification State
  const [selectedEtaMinutes, setSelectedEtaMinutes] = useState<number>(30);
  const [isProposingEta, setIsProposingEta] = useState(false);
  const [isConfirmingEta, setIsConfirmingEta] = useState(false);
  const [isCustomerConfirmingArrival, setIsCustomerConfirmingArrival] = useState(false);

  useEffect(() => {
    setJob(initialJob);
  }, [initialJob]);

  useEffect(() => {
    if (job?.id && (job.status === 'completed_by_professional' || job.status === 'professional_completed' || job.status === 'completed') && !tempCompletionPhoto) {
      fetchTemporaryCompletionPhoto(job.id).then((url) => {
        if (url) setTempCompletionPhoto(url);
      });
    }
  }, [job?.id, job?.status, tempCompletionPhoto]);

  const jobQuotes = (quotes || []).filter((q) => q.jobId === job.id);
  const myQuote = (quotes || []).find(
    (q) => q.jobId === job.id && q.professionalId === currentUser?.id
  );
  const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');

  const isOwner = currentUser?.id === job.customerId;
  const isProfessional = currentUser?.role === 'professional';
  const isAdmin = currentUser?.role === 'admin';
  const isAssignedToMe = isProfessional && (job.selectedProfessionalId === currentUser?.id || acceptedQuote?.professionalId === currentUser?.id);
  const canViewFullContact = isOwner || isAssignedToMe || isAdmin;

  const isCompleted = Boolean(
    job.status === 'completed' ||
    job.payment_released === true ||
    job.confirmed_by_customer === true ||
    job.completionStatus === 'confirmed' ||
    job.completionStatus === 'completed' ||
    (job as any).payment_status === 'released'
  );
  const isProCompleted = Boolean(
    !isCompleted &&
    (job.status === 'completed_by_professional' ||
      job.status === 'professional_completed' ||
      job.completed_by_professional === true ||
      job.completionStatus === 'completed_by_professional' ||
      job.completionStatus === 'awaiting_customer_confirmation')
  );
  const rawModalPayStatus = String(
    job.paymentStatus ||
      (job as any).payment_status ||
      (job.aiScopeEstimate as any)?.lifecycle_meta?.payment_status ||
      ''
  ).toLowerCase();
  const isAlreadyFundedInEscrow = Boolean(
    rawModalPayStatus === 'held_in_escrow' ||
      rawModalPayStatus === 'paid' ||
      rawModalPayStatus === 'released' ||
      job.escrow_held === true ||
      Boolean(job.payment_reference) ||
      job.status === 'in_progress' ||
      job.status === 'completed_by_professional' ||
      job.status === 'completed'
  );
  const isAwaitingEscrowPayment = Boolean(
    !isCompleted &&
      !isProCompleted &&
      !isAlreadyFundedInEscrow &&
      (rawModalPayStatus === 'awaiting_payment' ||
        rawModalPayStatus === 'pending' ||
        job.status === 'assigned')
  );
  const isInProgress = Boolean(
    !isCompleted &&
      !isProCompleted &&
      !isAwaitingEscrowPayment &&
      (job.status === 'assigned' || job.status === 'in_progress' || isAlreadyFundedInEscrow)
  );
  const hasCompletionPhotoProof = Boolean(
    tempCompletionPhoto ||
      job.completionPhotoUrl ||
      (job as any).completion_photo_url ||
      job.has_completion_photo ||
      (job as any).completion_photo_uploaded
  );

  // Customer Work Done Confirmation Handler
  const handleConfirmWorkDone = async () => {
    if (!currentUser?.id) return;
    setIsConfirmingCompletion(true);
    setCompletionFeedback(null);
    try {
      const res = await releaseJobPaymentRpc(job.id, completionNotes, currentUser.id);
      if (res.success) {
        setCompletionFeedback({
          type: 'success',
          text: res.message || 'Work confirmed! Payment released to professional available balance.',
        });
        setJob((prev) => ({
          ...prev,
          status: 'completed',
          completion_status: 'confirmed',
          confirmed_by_customer: true,
          completed_by_professional: true,
          payment_released: true,
          payment_released_at: new Date().toISOString(),
          customer_confirmed_at: new Date().toISOString(),
          completed_at: new Date().toISOString(),
          professional_earnings: res.releasedAmount,
        }));
        setIsConfirmDoneDialogOpen(false);
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
        // Prompt customer to rate artisan immediately after confirming
        setTimeout(() => {
          setIsRatingModalOpen(true);
        }, 500);
      } else {
        setCompletionFeedback({
          type: 'error',
          text: res.error || 'Failed to confirm job completion.',
        });
      }
    } catch (e: any) {
      setCompletionFeedback({
        type: 'error',
        text: e.message || 'Error communicating with server.',
      });
    } finally {
      setIsConfirmingCompletion(false);
    }
  };

  const hasAssignedPro = Boolean(
    job.selectedProfessionalId ||
    (job as any).selected_professional_id ||
    acceptedQuote?.professionalId
  );
  const isCancelled = job.status === 'cancelled';
  const isDisputed = job.status === 'disputed' || job.dispute_status === 'under_review';
  const isNoShowReported = job.no_show_status === 'reported';

  const handleOpenReassignModal = async () => {
    setIsReassignModalOpen(true);
    setLoadingCandidates(true);
    try {
      const res = await fetchReassignmentCandidatesApi(job.id);
      const candidates = res.candidates || [];
      setReassignmentCandidates(candidates);
      if (candidates.length > 0 && !selectedReplacementProId) {
        setSelectedReplacementProId(candidates[0].id);
      }
    } catch {
      setReassignmentCandidates([]);
    } finally {
      setLoadingCandidates(false);
    }
  };

  const handleCancelJobSubmit = async () => {
    if (!currentUser?.id) return;
    if (hasAssignedPro && (!cancelReason || cancelReason.trim().length < 5)) {
      setCompletionFeedback({
        type: 'error',
        text: 'Please provide a brief cancellation reason (at least 5 characters).',
      });
      return;
    }
    setIsCancellingJob(true);
    setCompletionFeedback(null);
    try {
      const res = await cancelJobApi(job.id, {
        customerId: currentUser.id,
        reason: cancelReason.trim() || 'Cancelled by customer before hiring',
        workStarted: hasAssignedPro ? cancelWorkStarted : false,
      });
      if (res.success) {
        setCompletionFeedback({
          type: 'success',
          text: res.message || 'Job request updated.',
        });
        const newStatus = res.outcome === 'routed_to_dispute' ? 'disputed' : 'cancelled';
        setJob((prev) => ({
          ...prev,
          status: newStatus,
          cancellation_reason: cancelReason.trim(),
          cancelled_at: new Date().toISOString(),
          payment_status: res.refundProcessed ? 'refunded' : prev.payment_status,
        }));
        setIsCancelModalOpen(false);
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
      } else {
        setCompletionFeedback({
          type: 'error',
          text: res.error || 'Could not cancel job.',
        });
      }
    } finally {
      setIsCancellingJob(false);
    }
  };

  const handleReportNoShowSubmit = async () => {
    if (!currentUser?.id) return;
    if (!noShowReason || noShowReason.trim().length < 5) {
      setCompletionFeedback({
        type: 'error',
        text: 'Please describe the no-show details (at least 5 characters).',
      });
      return;
    }
    setIsReportingNoShow(true);
    setCompletionFeedback(null);
    try {
      const res = await reportNoShowApi(job.id, {
        customerId: currentUser.id,
        reason: noShowReason.trim(),
      });
      if (res.success) {
        setCompletionFeedback({
          type: 'success',
          text: res.message || 'Professional no-show reported. Your escrow remains protected — you can now reassign to another verified artisan or cancel for a refund.',
        });
        setJob((prev) => ({
          ...prev,
          no_show_reported_at: new Date().toISOString(),
          no_show_reason: noShowReason.trim(),
          no_show_status: 'reported',
          escrow_frozen: true,
        }));
        setIsNoShowModalOpen(false);
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
      } else {
        setCompletionFeedback({
          type: 'error',
          text: res.error || 'Failed to report professional no-show.',
        });
      }
    } finally {
      setIsReportingNoShow(false);
    }
  };

  const handleReassignSubmit = async () => {
    if (!currentUser?.id || !selectedReplacementProId) return;
    setIsReassigningJob(true);
    setCompletionFeedback(null);
    try {
      const res = await reassignJobApi(job.id, {
        actorId: currentUser.id,
        actorRole: currentUser.role === 'admin' ? 'admin' : 'customer',
        newProfessionalId: selectedReplacementProId,
        reason: reassignReason.trim() || 'Reassigned due to professional no-show or unavailability',
      });
      if (res.success) {
        setCompletionFeedback({
          type: 'success',
          text: res.message || 'Job reassigned to replacement professional with existing escrow preserved.',
        });
        setJob((prev) => ({
          ...prev,
          status: 'in_progress',
          selectedProfessionalId: selectedReplacementProId,
          no_show_status: 'reassigned',
          escrow_frozen: false,
          reassignment_count: Number(prev.reassignment_count || 0) + 1,
        }));
        setIsReassignModalOpen(false);
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
      } else {
        setCompletionFeedback({
          type: 'error',
          text: res.error || 'Failed to reassign job.',
        });
      }
    } finally {
      setIsReassigningJob(false);
    }
  };

  const handleMarkArrived = async () => {
    if (!currentUser?.id) return;
    setIsMarkingArrived(true);
    setCompletionFeedback(null);
    try {
      const res = await markJobArrivedApi(job.id, {
        professionalId: currentUser.id,
        arrivalNotes: 'Professional confirmed on-site arrival via dashboard',
      });
      if (res.success) {
        setCompletionFeedback({
          type: 'success',
          text: 'Arrival recorded! The customer has been notified and no-show checks are cleared.',
        });
        setJob((prev) => ({
          ...prev,
          professional_arrived: true,
          professional_arrived_at: new Date().toISOString(),
          eta_status: 'arrived',
          no_show_status: prev.no_show_status === 'reported' ? 'resolved' : prev.no_show_status,
          escrow_frozen: false,
        }));
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
      } else {
        setCompletionFeedback({
          type: 'error',
          text: res.error || 'Failed to record arrival.',
        });
      }
    } finally {
      setIsMarkingArrived(false);
    }
  };

  const handleProposeEta = async () => {
    if (!currentUser?.id) return;
    setIsProposingEta(true);
    setCompletionFeedback(null);
    try {
      const res = await proposeJobArrivalEtaApi(job.id, {
        professionalId: currentUser.id,
        etaMinutes: selectedEtaMinutes,
      });
      if (res.success) {
        setCompletionFeedback({
          type: 'success',
          text: res.message || `Proposed arrival in ${selectedEtaMinutes} minutes. Waiting for customer confirmation.`,
        });
        setJob((prev) => ({
          ...prev,
          proposed_eta_at: res.proposedEtaAt || new Date(Date.now() + selectedEtaMinutes * 60000).toISOString(),
          proposed_eta_minutes: selectedEtaMinutes,
          eta_status: 'proposed',
        }));
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
      } else {
        setCompletionFeedback({
          type: 'error',
          text: res.error || 'Failed to propose arrival ETA.',
        });
      }
    } finally {
      setIsProposingEta(false);
    }
  };

  const handleConfirmEta = async () => {
    if (!currentUser?.id) return;
    setIsConfirmingEta(true);
    setCompletionFeedback(null);
    try {
      const res = await confirmJobArrivalEtaApi(job.id, {
        customerId: currentUser.id,
      });
      if (res.success) {
        setCompletionFeedback({
          type: 'success',
          text: res.message || 'Arrival time confirmed! Live arrival countdown is now active.',
        });
        setJob((prev) => ({
          ...prev,
          agreed_eta_at: res.agreedEtaAt || prev.proposed_eta_at || new Date(Date.now() + 30 * 60000).toISOString(),
          scheduled_arrival_at: res.agreedEtaAt || prev.scheduled_arrival_at,
          eta_confirmed_by_customer_at: new Date().toISOString(),
          eta_status: 'confirmed',
          grace_period_minutes: res.gracePeriodMinutes ?? 15,
        }));
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
      } else {
        setCompletionFeedback({
          type: 'error',
          text: res.error || 'Failed to confirm arrival ETA.',
        });
      }
    } finally {
      setIsConfirmingEta(false);
    }
  };

  const handleCustomerConfirmArrival = async () => {
    if (!currentUser?.id) return;
    setIsCustomerConfirmingArrival(true);
    setCompletionFeedback(null);
    try {
      const res = await confirmProfessionalArrivalByCustomerApi(job.id, {
        customerId: currentUser.id,
        notes: 'Customer confirmed professional arrived on site',
      });
      if (res.success) {
        setCompletionFeedback({
          type: 'success',
          text: res.message || 'Artisan arrival confirmed! Countdown stopped and work is underway.',
        });
        const nowIso = res.arrivedAt || new Date().toISOString();
        setJob((prev) => ({
          ...prev,
          customer_confirmed_arrival_at: nowIso,
          professional_arrived_at: prev.professional_arrived_at || nowIso,
          professional_arrived: true,
          eta_status: 'arrived',
          no_show_status: prev.no_show_status === 'reported' ? 'resolved' : prev.no_show_status,
          escrow_frozen: false,
        }));
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
      } else {
        setCompletionFeedback({
          type: 'error',
          text: res.error || 'Failed to confirm professional arrival.',
        });
      }
    } finally {
      setIsCustomerConfirmingArrival(false);
    }
  };

  const handleReportDelaySubmit = async () => {
    if (!currentUser?.id) return;
    setIsReportingDelay(true);
    setCompletionFeedback(null);
    try {
      const res = await reportJobDelayApi(job.id, {
        professionalId: currentUser.id,
        delayMinutes,
        reason: delayReason.trim() || 'Traffic or transit delay',
      });
      if (res.success) {
        setCompletionFeedback({
          type: 'success',
          text: `Delay of ${delayMinutes} minutes reported. Customer has been notified with the updated arrival estimate.`,
        });
        setJob((prev) => ({
          ...prev,
          delay_minutes: delayMinutes,
          delay_reason: delayReason.trim(),
          delay_reported_at: new Date().toISOString(),
          estimated_arrival_at: res.newEstimatedArrival || prev.estimated_arrival_at,
        }));
        setIsDelayModalOpen(false);
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
      } else {
        setCompletionFeedback({
          type: 'error',
          text: res.error || 'Failed to report delay.',
        });
      }
    } finally {
      setIsReportingDelay(false);
    }
  };

  const handleRescheduleSubmit = async () => {
    if (!currentUser?.id || !rescheduleTime) {
      setCompletionFeedback({
        type: 'error',
        text: 'Please select a future appointment date and time.',
      });
      return;
    }
    setIsRescheduling(true);
    setCompletionFeedback(null);
    try {
      const res = await rescheduleJobAppointmentApi(job.id, {
        actorId: currentUser.id,
        actorRole: currentUser.role === 'admin' ? 'admin' : isProfessional ? 'professional' : 'customer',
        newScheduledTime: rescheduleTime,
        reason: rescheduleReason.trim() || 'Appointment rescheduled',
      });
      if (res.success) {
        setCompletionFeedback({
          type: 'success',
          text: `Appointment rescheduled successfully.`,
        });
        setJob((prev) => ({
          ...prev,
          scheduled_arrival_at: rescheduleTime,
          estimated_arrival_at: rescheduleTime,
          rescheduled_at: new Date().toISOString(),
          rescheduled_reason: rescheduleReason.trim(),
          delay_minutes: 0,
          no_show_status: null,
          escrow_frozen: false,
        }));
        setIsRescheduleModalOpen(false);
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
      } else {
        setCompletionFeedback({
          type: 'error',
          text: res.error || 'Failed to reschedule appointment.',
        });
      }
    } finally {
      setIsRescheduling(false);
    }
  };

  // Safe Extraction of Fields
  const categorySlug = job.categorySlug || 'other';
  const categoryName = job.categoryName || job.aiAnalysis?.detected_category || 'Service Request';
  const rawDescription = job.description || job.aiAnalysis?.problem_summary || job.title || 'Service request submitted by customer.';
  const jobTitle = job.title || `${categoryName} in ${job.lga || 'Nigeria'}`;
  const budgetNum = Number(job.budgetEstimate || 0);

  // Safe AI Analysis Fields
  const ai = job.aiAnalysis;
  const scope = job.aiScopeEstimate;
  const requiredSkills = Array.isArray(ai?.required_skills) ? ai.required_skills : [];
  const possibleCauses = Array.isArray(ai?.possible_causes) ? ai.possible_causes : [];
  const rawFollowUpQuestions = Array.isArray(ai?.customer_clarification_questions)
    ? ai.customer_clarification_questions
    : Array.isArray(ai?.follow_up_questions)
    ? ai.follow_up_questions
    : [];
  // Defensive filter so legacy jobs never render professional-interview questions
  const followUpQuestions = rawFollowUpQuestions.filter((q) => {
    const s = String(q || '').toLowerCase();
    if (!s) return false;
    if (/\bdo you (have experience|carry|bring|offer|provide|use|supply)\b/i.test(s)) return false;
    if (/\bwhat (is your|warranty|diagnostic process)\b/i.test(s)) return false;
    if (/\bwill you (be performing|perform|replace|suggest|pressure-test)\b/i.test(s)) return false;
    if (/\b(board-level|component-level|full board replacement|power rails versus firmware)\b/i.test(s)) return false;
    return true;
  });
  const professionalPrepNotes = Array.isArray(ai?.professional_preparation_notes)
    ? ai.professional_preparation_notes
    : Array.isArray(scope?.professionalPreparationNotes)
    ? scope.professionalPreparationNotes
    : [];
  const unresolvedDetails = Array.isArray(ai?.unresolved_details_to_verify)
    ? ai.unresolved_details_to_verify
    : Array.isArray(scope?.unresolvedDetailsToVerify)
    ? scope.unresolvedDetailsToVerify
    : [];
  const knownJobRequirements = Array.isArray(ai?.known_job_requirements)
    ? ai.known_job_requirements
    : Array.isArray(scope?.knownJobRequirements)
    ? scope.knownJobRequirements
    : [];
  const customerAnswersEntries =
    ai?.conversation_answers && typeof ai.conversation_answers === 'object'
      ? Object.entries(ai.conversation_answers).filter(([, ans]) => typeof ans === 'string' && ans.trim().length > 0)
      : [];
  const matchingKeywords = Array.isArray(ai?.matching_keywords) ? ai.matching_keywords : [];

  // Safe AI Scope Fields
  const recommendedMaterials = Array.isArray(scope?.recommendedMaterials) ? scope.recommendedMaterials : [];
  const budgetMin = scope?.estimatedBudgetNaira && typeof scope.estimatedBudgetNaira.min === 'number'
    ? scope.estimatedBudgetNaira.min
    : (budgetNum > 0 ? Math.round(budgetNum * 0.8) : null);
  const budgetMax = scope?.estimatedBudgetNaira && typeof scope.estimatedBudgetNaira.max === 'number'
    ? scope.estimatedBudgetNaira.max
    : (budgetNum > 0 ? Math.round(budgetNum * 1.3) : null);

  // Safe Media Arrays
  const photos = Array.isArray(job.photos) ? job.photos.filter(Boolean) : [];
  const videos = Array.isArray(job.videos) ? job.videos.filter(Boolean) : [];

  // Safe Date Formatting
  const formattedDate = (() => {
    try {
      if (!job.createdAt) return 'Recently';
      const d = new Date(job.createdAt);
      if (isNaN(d.getTime())) return 'Recently';
      return d.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return 'Recently';
    }
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/75 backdrop-blur-xs animate-in fade-in">
      <div className="bg-white rounded-2xl sm:rounded-3xl max-w-3xl w-full p-4 sm:p-7 border border-slate-200 shadow-2xl relative max-h-[92vh] overflow-y-auto space-y-4 sm:space-y-6">
        {/* Top Header Actions (Share & Close) */}
        <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
          <ShareJobButton job={job} variant="compact" showDropdown={true} />
          <button
            onClick={onClose}
            className="p-2 rounded-full text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Top Header */}
        <div className="pr-36 sm:pr-48 space-y-2">
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <span className="px-2.5 py-0.5 sm:py-1 rounded-lg text-[11px] sm:text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center gap-1.5">
              <CategoryIcon slug={categorySlug} className="w-3.5 h-3.5" />
              {categoryName}
            </span>

            {job.status === 'open' ? (
              <span className="px-2.5 py-0.5 sm:py-1 rounded-lg text-[11px] sm:text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
                🟢 Open for Quotes
              </span>
            ) : isInProgress ? (
              <span className="px-2.5 py-0.5 sm:py-1 rounded-lg text-[11px] sm:text-xs font-bold bg-blue-50 text-blue-800 border border-blue-200">
                🔵 Job In Progress
              </span>
            ) : isProCompleted ? (
              <span className="px-2.5 py-0.5 sm:py-1 rounded-lg text-[11px] sm:text-xs font-bold bg-purple-100 text-purple-900 border border-purple-300">
                {isProfessional ? '🟣 Waiting for Customer Confirmation' : '🟣 Professional Marked Job Completed'}
              </span>
            ) : isCompleted ? (
              <span className="px-2.5 py-0.5 sm:py-1 rounded-lg text-[11px] sm:text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                ✅ Job Completed
              </span>
            ) : (
              <span className="px-2.5 py-0.5 sm:py-1 rounded-lg text-[11px] sm:text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                {job.status}
              </span>
            )}

            {isCompleted && (
              <span className="px-2.5 py-0.5 sm:py-1 rounded-lg text-[11px] sm:text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                💰 Payment Released
              </span>
            )}

            {job.urgency === 'emergency' ? (
              <span className="px-2 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
                🚨 Emergency Repair
              </span>
            ) : job.urgency === 'within_48h' ? (
              <span className="px-2 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                ⚡ Within 48 Hours
              </span>
            ) : (
              <span className="px-2 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-medium bg-slate-100 text-slate-600">
                🗓️ Flexible Schedule
              </span>
            )}

            <span className="text-[11px] sm:text-xs text-slate-400">
              Posted {formattedDate}
            </span>
          </div>

          <h2 className="text-lg sm:text-2xl font-black text-slate-900 leading-snug">
            {jobTitle}
          </h2>

          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-slate-600">
            <span className="flex items-center gap-1.5 font-medium text-slate-700">
              <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>
                {job.cityArea ? `${job.cityArea}, ` : ''}{job.lga || 'Ikeja'} LGA, {job.state || 'Lagos'} State
              </span>
            </span>
            <span className="text-slate-400">•</span>
            {(() => {
              const custProf = profiles.find((p) => p.id === job.customerId);
              const resolvedCustomerName =
                job.customerName && job.customerName !== 'Customer'
                  ? job.customerName
                  : custProf?.fullName || job.customerName || 'Customer';
              const resolvedCustomerPhone =
                cleanContactPhone(job.customerPhone) ||
                cleanContactPhone(custProf?.phone) ||
                cleanContactPhone((job.aiScopeEstimate as any)?.customer_meta?.customerPhone);
              const effectiveAssignedProId =
                job.selectedProfessionalId ||
                (job as any).selected_professional_id ||
                acceptedQuote?.professionalId;
              const assignedProProf = effectiveAssignedProId
                ? profiles.find((p) => p.id === effectiveAssignedProId)
                : undefined;
              const resolvedProPhone =
                cleanContactPhone(job.selectedProfessionalPhone) ||
                cleanContactPhone(assignedProProf?.phone) ||
                cleanContactPhone(acceptedQuote?.professionalPhone);

              return (
                <>
                  <span className="flex items-center gap-1 text-slate-700">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    <span>Customer: <strong>{resolvedCustomerName}</strong></span>
                  </span>
                  {canViewFullContact && resolvedCustomerPhone && (
                    <>
                      <span className="text-slate-400">•</span>
                      <a
                        href={`tel:${resolvedCustomerPhone}`}
                        className="flex items-center gap-1 text-emerald-700 hover:underline font-semibold"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        <span>{resolvedCustomerPhone}</span>
                      </a>
                    </>
                  )}
                  {(isOwner || isAdmin) && effectiveAssignedProId && resolvedProPhone && (
                    <>
                      <span className="text-slate-400">•</span>
                      <a
                        href={`tel:${resolvedProPhone}`}
                        className="flex items-center gap-1 text-indigo-700 hover:underline font-semibold"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        <span>Artisan: {resolvedProPhone}</span>
                      </a>
                    </>
                  )}
                </>
              );
            })()}
          </div>

          {/* Real-Time Job Status Progression Tracker */}
          <RealTimeJobStatusTracker job={job} quotes={quotes} onRefresh={refreshJobsAndQuotes} onRateJob={() => setIsRatingModalOpen(true)} />

          {/* Feedback Banner */}
          {completionFeedback && (
            <div
              className={`p-3.5 rounded-2xl border text-xs font-bold flex items-center justify-between gap-2 ${
                completionFeedback.type === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-rose-50 border-rose-200 text-rose-900'
              }`}
            >
              <span>{completionFeedback.text}</span>
              <button
                type="button"
                onClick={() => setCompletionFeedback(null)}
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                ✕
              </button>
            </div>
          )}

          {/* Open Job with 0 Quotes Matching Indicator */}
          {job.status === 'open' && jobQuotes.length === 0 && (
            <div className="p-3.5 rounded-2xl bg-amber-50/80 border border-amber-200 text-xs text-amber-950 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                <div>
                  <span className="font-extrabold block">No quotes yet — Matching verified professionals in your area</span>
                  <span className="text-[11px] text-amber-800">
                    Estimated first quote response time: 15–30 mins. Verified artisans in {job.lga}, {job.state} have been alerted.
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* No-Show Reported Banner with Immediate Reassign or Cancel & Refund Actions */}
          {isNoShowReported && !isCancelled && !isCompleted && (
            <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-xs text-rose-950 space-y-2.5">
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-black block">Professional No-Show Reported — Escrow Protected 🔒</span>
                  <p className="text-[11px] text-rose-800 mt-0.5">
                    Payout to the non-performing professional is blocked. You can reassign this job to another verified artisan at no extra cost, or cancel for an escrow refund.
                  </p>
                </div>
              </div>
              {(isOwner || isAdmin) && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleOpenReassignModal}
                    className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs cursor-pointer"
                  >
                    Reassign to Another Verified Artisan (No Extra Charge)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCancelWorkStarted(false);
                      setCancelReason(job.no_show_reason || 'Hired professional failed to show up');
                      setIsCancelModalOpen(true);
                    }}
                    className="px-3.5 py-2 rounded-xl bg-white hover:bg-rose-100 text-rose-800 border border-rose-300 font-bold text-xs cursor-pointer"
                  >
                    Cancel Job & Refund Escrow
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Auto-Reassigned Banner */}
          {job.auto_reassigned && !isCancelled && (
            <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-300 text-xs text-emerald-950 flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-black block">Automatically Reassigned — Replacement Artisan Dispatched</span>
                <span className="text-[11px] text-emerald-900">
                  The original artisan missed their arrival window. JobMatch NG automatically reassigned this job with zero additional charges and 100% escrow protection.
                  {job.estimated_arrival_at && ` Estimated arrival: ${new Date(job.estimated_arrival_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} WAT.`}
                </span>
              </div>
            </div>
          )}

          {/* Pending Auto-Reassignment Queue Banner */}
          {job.no_show_status === 'pending_reassignment' && !isCancelled && (
            <div className="p-3.5 rounded-2xl bg-amber-50 border border-amber-300 text-xs text-amber-950 flex items-start gap-2.5">
              <Clock className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <span className="font-black block">Matching Replacement Artisan...</span>
                <span className="text-[11px] text-amber-900">
                  The original artisan missed the scheduled arrival window. Our automated dispatcher is currently locating the next available verified artisan in {job.lga}, {job.state}. Payout is frozen and your escrow is 100% safe.
                </span>
              </div>
            </div>
          )}

          {/* Escrow Payment Gate Banner (Awaiting Customer Paystack Payment) */}
          {isAwaitingEscrowPayment && !isCancelled && (
            <div className="p-4 rounded-2xl bg-amber-50 border-2 border-amber-300 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
              <div className="space-y-1">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-amber-200/80 text-amber-950 font-black uppercase tracking-wider text-[10px]">
                  <Clock className="w-3 h-3" /> Awaiting Paystack Escrow Payment
                </span>
                <span className="font-black text-slate-900 block text-sm">
                  {isAssignedToMe
                    ? 'Do Not Start Work Yet — Waiting for Customer Escrow Payment'
                    : 'Complete Paystack Escrow Payment to Authorize Work'}
                </span>
                <span className="text-[11px] text-slate-700 block">
                  {isAssignedToMe
                    ? 'Arrival and job completion actions are locked until the server verifies the customer’s Paystack escrow payment.'
                    : 'Your professional is ready. Complete your Paystack escrow payment so work can begin safely.'}
                </span>
              </div>
              {isOwner && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigateTo('job_details', {
                      jobId: job.id,
                      quoteId: acceptedQuote?.id || job.acceptedQuoteId,
                      pay: 'true',
                    });
                  }}
                  className="shrink-0 px-4 py-2.5 rounded-xl bg-[#008751] hover:bg-[#006b3f] text-white font-extrabold text-xs cursor-pointer shadow-xs"
                >
                  Complete Escrow Payment
                </button>
              )}
            </div>
          )}

          {/* 60-Minute ETA Proposal & Arrival Confirmation Action Card */}
          {isInProgress && !job.professional_arrived && !job.professional_arrived_at && !job.customer_confirmed_arrival_at && !isCancelled && (
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3 text-xs">
              {isAssignedToMe && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <span className="font-black text-slate-900 block">
                      {job.agreed_eta_at
                        ? `Agreed Arrival Time: ${new Date(job.agreed_eta_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} WAT`
                        : 'Propose Your Estimated Arrival Time (Max 60 Mins)'}
                    </span>
                    <span className="text-[11px] text-slate-600">
                      {job.agreed_eta_at
                        ? 'Click "I Have Arrived" as soon as you reach the customer location.'
                        : 'Select your arrival ETA within the 60-minute post-acceptance window for customer confirmation.'}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!job.agreed_eta_at && (
                      <>
                        <select
                          value={selectedEtaMinutes}
                          onChange={(e) => setSelectedEtaMinutes(Number(e.target.value))}
                          className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 font-bold text-xs"
                        >
                          <option value={15}>Arrive in 15 mins</option>
                          <option value={30}>Arrive in 30 mins</option>
                          <option value={45}>Arrive in 45 mins</option>
                          <option value={60}>Arrive in 60 mins</option>
                        </select>
                        <button
                          type="button"
                          onClick={handleProposeEta}
                          disabled={isProposingEta}
                          className="px-3.5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs cursor-pointer disabled:opacity-60"
                        >
                          {isProposingEta ? 'Sending...' : job.proposed_eta_at ? 'Update ETA' : 'Propose ETA'}
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={handleMarkArrived}
                      disabled={isMarkingArrived}
                      className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs cursor-pointer disabled:opacity-60 flex items-center gap-1.5"
                    >
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{isMarkingArrived ? 'Confirming...' : 'I Have Arrived'}</span>
                    </button>
                  </div>
                </div>
              )}

              {isOwner && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <span className="font-black text-slate-900 block">
                      {job.agreed_eta_at
                        ? `Agreed Artisan Arrival: ${new Date(job.agreed_eta_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} WAT`
                        : job.proposed_eta_minutes
                        ? `Artisan Proposed Arrival in ~${job.proposed_eta_minutes} Minutes`
                        : 'Awaiting Artisan ETA Proposal (60-Min Window)'}
                    </span>
                    <span className="text-[11px] text-slate-600">
                      {job.agreed_eta_at
                        ? 'Once your artisan reaches your location, click "Confirm Artisan Arrived" to stop the arrival timer.'
                        : job.proposed_eta_minutes
                        ? 'Confirm the proposed ETA to start the live arrival countdown.'
                        : 'Your assigned artisan has 60 minutes from acceptance to propose their arrival time.'}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!job.agreed_eta_at && (job.proposed_eta_at || job.proposed_eta_minutes) && (
                      <button
                        type="button"
                        onClick={handleConfirmEta}
                        disabled={isConfirmingEta}
                        className="px-3.5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs cursor-pointer disabled:opacity-60"
                      >
                        {isConfirmingEta ? 'Confirming...' : `Confirm ETA (~${job.proposed_eta_minutes || 30}m)`}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleCustomerConfirmArrival}
                      disabled={isCustomerConfirmingArrival}
                      className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs cursor-pointer disabled:opacity-60 flex items-center gap-1.5"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{isCustomerConfirmingArrival ? 'Confirming...' : 'Confirm Artisan Arrived'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Artisan On-Site Arrival Banner */}
          {(job.professional_arrived || job.professional_arrived_at || job.customer_confirmed_arrival_at) && !isCompleted && !isCancelled && (
            <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-black block">Artisan Confirmed On Site</span>
                <span className="text-[11px] text-emerald-800">
                  Arrival verified at {new Date(job.customer_confirmed_arrival_at || job.professional_arrived_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} WAT. No-show checks are cleared.
                </span>
              </div>
            </div>
          )}

          {/* Transit Delay Reported Banner */}
          {Boolean(job.delay_minutes) && !job.professional_arrived && !isCompleted && !isCancelled && (
            <div className="p-3.5 rounded-2xl bg-amber-50 border border-amber-200 text-xs text-amber-950 flex items-start gap-2.5">
              <Clock className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-black block">Artisan Transit Delay Reported (+{job.delay_minutes} mins)</span>
                <span className="text-[11px] text-amber-900">
                  {job.delay_reason || 'Traffic or transit delay'}. New estimated arrival: {job.estimated_arrival_at ? new Date(job.estimated_arrival_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' WAT' : 'Updated shortly'}.
                </span>
              </div>
            </div>
          )}

          {/* Disputed Banner */}
          {isDisputed && (
            <div className="p-3.5 rounded-2xl bg-amber-50 border border-amber-300 text-xs text-amber-950 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <span className="font-black block">Under Admin Dispute & Escrow Review</span>
                <span className="text-[11px] text-amber-900">
                  Escrow funds are frozen while JobMatch NG Support reviews this cancellation request. Reason: {job.cancellation_reason || 'Work already started dispute review'}.
                </span>
              </div>
            </div>
          )}

          {/* Cancelled Banner */}
          {isCancelled && (
            <div className="p-3.5 rounded-2xl bg-slate-100 border border-slate-300 text-xs text-slate-800 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-black block">Job Cancelled</span>
                <span className="text-[11px] text-slate-600">
                  {job.cancellation_reason ? `Reason: ${job.cancellation_reason}` : 'This job request was cancelled.'}
                  {job.payment_status === 'refunded' ? ' • Escrow Refund Initiated ✅' : ''}
                </span>
              </div>
            </div>
          )}

          {/* Privacy-controlled Address & Exact Job Location Section */}
          {canViewFullContact ? (
            <div className="p-3.5 rounded-2xl bg-emerald-50/50 border border-emerald-200/80 text-xs space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-extrabold text-emerald-950 block">Job Site Address:</span>
                    <span className="text-slate-800 font-medium">
                      {job.address || job.formattedAddress || `${job.cityArea ? `${job.cityArea}, ` : ''}${job.lga || 'Ikeja'}, ${job.state || 'Lagos'}`}
                    </span>
                  </div>
                </div>

                {/* Open in Google Maps */}
                {(() => {
                  const mapsUrl = job.locationLatitude && job.locationLongitude
                    ? `https://www.google.com/maps/search/?api=1&query=${job.locationLatitude},${job.locationLongitude}`
                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        `${job.address || job.formattedAddress || ''}, ${job.cityArea ? job.cityArea + ', ' : ''}${job.lga}, ${job.state}, Nigeria`
                      )}`;
                  return (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-2xs shrink-0 transition-colors w-fit"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span>Open in Google Maps</span>
                    </a>
                  );
                })()}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-slate-600 pt-1.5 border-t border-emerald-200/50">
                <div>
                  <span className="text-slate-500">LGA & State:</span>{' '}
                  <strong className="text-slate-800">
                    {job.cityArea ? `${job.cityArea}, ` : ''}{job.lga} LGA, {job.state} State
                  </strong>
                </div>
                {job.locationLatitude && job.locationLongitude && (
                  <div>
                    <span className="text-slate-500">Coordinates:</span>{' '}
                    <span className="font-mono text-slate-700">
                      {job.locationLatitude.toFixed(5)}, {job.locationLongitude.toFixed(5)}
                    </span>
                  </div>
                )}
                {job.locationLandmark && (
                  <div className="sm:col-span-2 p-2 rounded-xl bg-amber-50 border border-amber-200/70 text-amber-950">
                    <span className="font-bold text-amber-900">Landmark & Directions:</span>{' '}
                    <span>{job.locationLandmark}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-xs flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-700 shrink-0">General Area:</span>
                <span className="text-slate-600">
                  {job.cityArea ? `${job.cityArea}, ` : ''}{job.lga || 'Ikeja'}, ${job.state || 'Lagos'} State
                </span>
              </div>
              <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md font-medium border border-amber-200 shrink-0">
                🔒 Exact street revealed upon quote acceptance
              </span>
            </div>
          )}
        </div>

        {/* Budget & Quotes Summary Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100 text-xs">
          <div>
            <span className="text-[10px] uppercase text-slate-400 block font-semibold">
              Target Budget
            </span>
            <span className="text-lg font-black text-emerald-800">
              ₦{budgetNum > 0 ? budgetNum.toLocaleString() : 'Negotiable'}
            </span>
          </div>

          <div>
            <span className="text-[10px] uppercase text-slate-400 block font-semibold">
              Quotes Received
            </span>
            <span className="text-lg font-black text-slate-900">
              {jobQuotes.length}
            </span>
          </div>

          <div className="col-span-2 sm:col-span-1">
            <span className="text-[10px] uppercase text-slate-400 block font-semibold">
              Timing Required
            </span>
            <span className="text-xs font-bold text-slate-800">
              {job.urgency === 'emergency'
                ? 'Immediate / Today'
                : job.urgency === 'within_48h'
                ? 'Within 48 Hours'
                : 'Flexible Schedule'}
            </span>
          </div>
        </div>

        {/* Customer Original Job Description & Request */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-emerald-600" />
              <span>Customer's Original Description & Request</span>
            </h3>
            <span className="text-[10px] text-slate-400 font-semibold">Authoritative Customer Input</span>
          </div>
          <div className="p-4 rounded-2xl bg-white border border-slate-200 text-xs sm:text-sm text-slate-800 leading-relaxed whitespace-pre-line shadow-xs font-normal">
            {rawDescription}
          </div>
        </div>

        {/* Temporary Completion Proof (if job was marked done by pro) */}
        {tempCompletionPhoto && (
          <div className="p-4 rounded-2xl bg-purple-50 border border-purple-200 space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-purple-950">
              <CheckCircle2 className="w-4 h-4 text-purple-700" />
              <span>Professional Completion Proof & Photo Submission</span>
            </div>
            <div className="rounded-xl overflow-hidden border border-purple-200 bg-slate-900 max-h-72 flex items-center justify-center">
              <img
                src={tempCompletionPhoto}
                alt="Job Completion Proof"
                className="max-h-72 w-full object-contain"
              />
            </div>
            {job.completionNotes && (
              <p className="text-xs text-purple-900 bg-white p-3 rounded-xl border border-purple-100 italic">
                "{job.completionNotes}"
              </p>
            )}
          </div>
        )}

        {/* Uploaded Site Media (Photos & Videos) */}
        {(photos.length > 0 || videos.length > 0) && (
          <div className="space-y-3">
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
              <ImageIcon className="w-4 h-4 text-emerald-600" />
              <span>Site Media Attached ({photos.length} Photos, {videos.length} Videos)</span>
            </h3>

            <div className="space-y-3">
              {/* Photos */}
              {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {photos.map((url, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedMediaIndex(idx)}
                      className="group relative rounded-2xl overflow-hidden aspect-video bg-slate-100 border border-slate-200 cursor-pointer hover:border-emerald-500 transition-all shadow-xs"
                    >
                      <img
                        src={url}
                        alt={`Site photo ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                        <Maximize2 className="w-5 h-5" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Videos */}
              {videos.length > 0 && (
                <div className="space-y-2">
                  <span className="text-[11px] font-bold text-slate-600 block">Uploaded Videos:</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {videos.map((vidUrl, vIdx) => (
                      <div
                        key={vIdx}
                        className="rounded-2xl overflow-hidden bg-black border border-slate-200 aspect-video shadow-xs"
                      >
                        <video
                          src={vidUrl}
                          controls
                          className="w-full h-full object-contain"
                          poster={photos[0]}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI Diagnostic Analysis Card (Safe Render) */}
        {ai && (
          <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-emerald-50/80 via-teal-50/50 to-slate-50 border border-emerald-200 space-y-3.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <span className="font-extrabold text-xs text-slate-900">
                  JobMatch NG Master Engineering AI Diagnosis
                </span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {(ai.severity_score || scope?.severityScore) && (
                  <span
                    className={`px-2.5 py-0.5 rounded-md text-[10px] font-extrabold ${
                      (ai.severity_score || scope?.severityScore) === 'Critical Hazard' ||
                      (ai.severity_score || scope?.severityScore) === 'High'
                        ? 'bg-rose-100 text-rose-800 border border-rose-200'
                        : 'bg-amber-100 text-amber-800 border border-amber-200'
                    }`}
                  >
                    Severity: {ai.severity_score || scope?.severityScore}
                  </span>
                )}
                {ai.detected_category && (
                  <span className="px-2.5 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                    Recommended Trade: {ai.detected_category}
                  </span>
                )}
              </div>
            </div>

            {ai.problem_summary && (
              <p className="text-xs text-slate-700 bg-white p-3 rounded-xl border border-emerald-100 leading-relaxed font-medium">
                {ai.problem_summary}
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {requiredSkills.length > 0 && (
                <div className="bg-white p-3 rounded-xl border border-emerald-100 space-y-1.5">
                  <span className="text-[10px] text-slate-400 font-bold block uppercase">
                    Required Skills / Trade:
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {requiredSkills.map((skill, sIdx) => (
                      <span
                        key={sIdx}
                        className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[10px] font-semibold"
                      >
                        ✓ {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {ai.estimated_job_complexity && (
                <div className="bg-white p-3 rounded-xl border border-emerald-100 flex flex-col justify-between">
                  <div>
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      Technical Complexity:
                    </span>
                    <span className="font-extrabold text-slate-800 capitalize">
                      {ai.estimated_job_complexity} Level
                    </span>
                  </div>
                  {matchingKeywords.length > 0 && (
                    <div className="mt-2 text-[10px] text-slate-400 truncate">
                      Tags: {matchingKeywords.slice(0, 4).join(', ')}
                    </div>
                  )}
                </div>
              )}

              {(Array.isArray(ai.root_cause_hypotheses) && ai.root_cause_hypotheses.length > 0) ||
              (Array.isArray(scope?.rootCauseHypotheses) && scope.rootCauseHypotheses.length > 0) ? (
                <div className="bg-white p-3.5 rounded-xl border border-emerald-100 sm:col-span-2 space-y-2">
                  <span className="text-[10px] text-emerald-900 font-extrabold block uppercase">
                    🔬 Possible Causes to Inspect On Site (Unconfirmed):
                  </span>
                  <div className="space-y-1.5">
                    {(ai.root_cause_hypotheses || scope?.rootCauseHypotheses || []).map((hyp, hIdx) => (
                      <div key={hIdx} className="p-2.5 rounded-lg bg-slate-50 border border-slate-200/80 space-y-0.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-slate-900 text-xs">{hyp.cause}</span>
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                              hyp.likelihood === 'High'
                                ? 'bg-emerald-100 text-emerald-800'
                                : hyp.likelihood === 'Medium'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-slate-200 text-slate-700'
                            }`}
                          >
                            {hyp.likelihood} Likelihood
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-600 leading-relaxed">{hyp.technicalExplanation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                possibleCauses.length > 0 && (
                  <div className="bg-white p-3 rounded-xl border border-emerald-100 sm:col-span-2 space-y-1">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase">
                      Possible Causes (Require On-Site Verification):
                    </span>
                    <ul className="space-y-1 text-xs text-slate-700">
                      {possibleCauses.map((cause, cIdx) => (
                        <li key={cIdx} className="flex items-start gap-1.5">
                          <span className="text-emerald-600 font-bold">•</span>
                          <span>{cause}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              )}

              {knownJobRequirements.length > 0 && (
                <div className="bg-white p-3 rounded-xl border border-emerald-100 sm:col-span-2 space-y-1.5">
                  <span className="text-[10px] text-slate-500 font-extrabold block uppercase">
                    📌 Known Job Requirements & Reported Details:
                  </span>
                  <ul className="space-y-1 text-[11px] text-slate-700">
                    {knownJobRequirements.map((reqItem, rIdx) => (
                      <li key={rIdx} className="flex items-start gap-1.5">
                        <span className="text-emerald-600 font-bold">•</span>
                        <span>{reqItem}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {professionalPrepNotes.length > 0 && (
                <div className="bg-white p-3 rounded-xl border border-emerald-100 space-y-1.5">
                  <span className="text-[10px] text-slate-500 font-extrabold block uppercase">
                    📋 Professional Preparation Notes:
                  </span>
                  <ul className="space-y-1 text-[11px] text-slate-700">
                    {professionalPrepNotes.map((note, nIdx) => (
                      <li key={nIdx} className="flex items-start gap-1.5 leading-relaxed">
                        <span className="text-emerald-600 font-bold">•</span>
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {unresolvedDetails.length > 0 && (
                <div className="bg-white p-3 rounded-xl border border-emerald-100 space-y-1.5">
                  <span className="text-[10px] text-slate-500 font-extrabold block uppercase">
                    🔍 Unresolved Details to Verify During Visit:
                  </span>
                  <ul className="space-y-1 text-[11px] text-slate-700">
                    {unresolvedDetails.map((detail, dIdx) => (
                      <li key={dIdx} className="flex items-start gap-1.5 leading-relaxed">
                        <span className="text-amber-600 font-bold">•</span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {((Array.isArray(ai.inspection_protocol) && ai.inspection_protocol.length > 0) ||
                (Array.isArray(scope?.inspectionProtocol) && scope.inspectionProtocol.length > 0)) && (
                <div className="bg-white p-3 rounded-xl border border-emerald-100 space-y-1.5">
                  <span className="text-[10px] text-slate-500 font-extrabold block uppercase">
                    🛠️ On-Site Inspection Protocol:
                  </span>
                  <ol className="space-y-1 text-[11px] text-slate-700 list-decimal list-inside">
                    {(ai.inspection_protocol || scope?.inspectionProtocol || []).map((step, idx) => (
                      <li key={idx} className="leading-relaxed">
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {((Array.isArray(ai.tools_and_equipment) && ai.tools_and_equipment.length > 0) ||
                (Array.isArray(scope?.toolsAndEquipment) && scope.toolsAndEquipment.length > 0)) && (
                <div className="bg-white p-3 rounded-xl border border-emerald-100 space-y-1.5">
                  <span className="text-[10px] text-slate-500 font-extrabold block uppercase">
                    🧰 Tools & Diagnostic Equipment to Consider:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {(ai.tools_and_equipment || scope?.toolsAndEquipment || []).map((tool, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200 text-slate-800 text-[11px] font-medium"
                      >
                        🔧 {tool}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {customerAnswersEntries.length > 0 && (
              <div className="bg-white p-3 rounded-xl border border-emerald-200 space-y-1.5">
                <span className="text-[10px] font-extrabold text-emerald-900 uppercase tracking-wider block">
                  ✅ Customer Clarifications Provided During Job Intake:
                </span>
                <div className="space-y-1.5 text-xs text-slate-700">
                  {customerAnswersEntries.map(([question, answer], aIdx) => (
                    <div key={aIdx} className="p-2 rounded-lg bg-emerald-50/50 border border-emerald-100">
                      <span className="font-semibold text-slate-700 block text-[11px]">{question}</span>
                      <span className="font-bold text-emerald-950 text-xs">{answer}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {followUpQuestions.length > 0 && (
              <div className="bg-white p-3 rounded-xl border border-emerald-100 space-y-1.5">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                  Customer Clarification Questions (Ask Customer if Needed Before Visiting):
                </span>
                <ul className="space-y-1 text-xs text-slate-700">
                  {followUpQuestions.map((q, qIdx) => (
                    <li key={qIdx} className="flex items-start gap-1.5">
                      <span className="text-emerald-600 font-bold">•</span>
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* AI Scope Assessment & Material Guide (Safe Render) */}
        {scope && (
          <div className="p-4 sm:p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-600" />
              <span className="font-bold text-xs text-slate-900">
                Scope Analysis & Nigerian Material Guide
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {scope.laborEstimateDays && (
                <div className="bg-white p-3 rounded-xl border border-slate-200">
                  <span className="text-[10px] text-slate-400 font-bold block mb-1 uppercase">
                    Estimated Duration:
                  </span>
                  <span className="font-extrabold text-slate-800">
                    {scope.laborEstimateDays}
                  </span>
                </div>
              )}

              {(budgetMin !== null || budgetMax !== null) && (
                <div className="bg-white p-3 rounded-xl border border-slate-200">
                  <span className="text-[10px] text-slate-400 font-bold block mb-1 uppercase">
                    Recommended Naira Range:
                  </span>
                  <span className="font-extrabold text-emerald-800">
                    ₦{(budgetMin || 0).toLocaleString()} - ₦{(budgetMax || 0).toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            {scope.itemizedCostBreakdown && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
                <div className="bg-white p-2.5 rounded-xl border border-slate-200">
                  <span className="text-[10px] text-slate-400 font-bold uppercase block">Diagnostic Visit Fee</span>
                  <span className="font-extrabold text-slate-900">
                    ₦{Number(scope.itemizedCostBreakdown.diagnosticVisitFee || 5000).toLocaleString()}
                  </span>
                </div>
                <div className="bg-white p-2.5 rounded-xl border border-slate-200">
                  <span className="text-[10px] text-slate-400 font-bold uppercase block">Skilled Labor Range</span>
                  <span className="font-extrabold text-slate-900">
                    ₦{Number(scope.itemizedCostBreakdown.laborMin || 8000).toLocaleString()} – ₦{Number(scope.itemizedCostBreakdown.laborMax || 20000).toLocaleString()}
                  </span>
                </div>
                <div className="bg-white p-2.5 rounded-xl border border-slate-200">
                  <span className="text-[10px] text-slate-400 font-bold uppercase block">Estimated Parts Range</span>
                  <span className="font-extrabold text-slate-900">
                    ₦{Number(scope.itemizedCostBreakdown.materialsMin || 4000).toLocaleString()} – ₦{Number(scope.itemizedCostBreakdown.materialsMax || 15000).toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {recommendedMaterials.length > 0 && (
              <div className="text-xs text-slate-700 bg-white p-3 rounded-xl border border-slate-200">
                <span className="font-semibold block mb-1.5">Recommended Parts & Consumables:</span>
                <div className="flex flex-wrap gap-1.5">
                  {recommendedMaterials.map((mat, mIdx) => (
                    <span
                      key={mIdx}
                      className="px-2 py-0.5 rounded-md bg-slate-50 text-slate-700 border border-slate-200 text-[11px]"
                    >
                      {mat}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(ai?.preventive_maintenance_advice || scope.preventiveMaintenanceAdvice) && (
              <div className="p-3 rounded-xl bg-emerald-50/70 border border-emerald-200 text-[11px] text-emerald-950">
                <strong className="font-extrabold">🛡️ Preventive Engineering Advice:</strong>{' '}
                {ai?.preventive_maintenance_advice || scope.preventiveMaintenanceAdvice}
              </div>
            )}
          </div>
        )}

        {/* Bottom Action Buttons */}
        <div className="pt-4 border-t border-slate-100 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-colors min-h-[44px] cursor-pointer"
            >
              Close Details
            </button>
            {(isOwner || isAssignedToMe) && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigateTo('messages', { jobId: job.id });
                }}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs transition-colors min-h-[44px] cursor-pointer"
              >
                Messages
              </button>
            )}
            <ShareJobButton job={job} variant="outline" showDropdown={true} />
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {/* If Customer Owner & In Progress or Pro has not sent completion photo yet */}
            {isOwner && ((isInProgress && !isProCompleted) || (isProCompleted && !hasCompletionPhotoProof)) && (
              <button
                type="button"
                disabled={true}
                title="Awaiting professional to finish job and upload a photo of the completed work before releasing payment"
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-slate-200 text-slate-500 font-extrabold text-xs flex items-center justify-center gap-1.5 cursor-not-allowed opacity-85 min-h-[44px]"
              >
                <CheckCircle2 className="w-4 h-4 text-slate-400" />
                <span>
                  {isProCompleted && !hasCompletionPhotoProof
                    ? 'Awaiting Completion Photo from Pro'
                    : 'Release Payment (Unlocks After Pro Sends Job Photo)'}
                </span>
              </button>
            )}

            {/* If Customer Owner & Pro marked completed with photo proof */}
            {isOwner && isProCompleted && hasCompletionPhotoProof && (
              <button
                type="button"
                onClick={() => setIsConfirmDoneDialogOpen(true)}
                disabled={false}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-xs flex items-center justify-center gap-1.5 transition-colors min-h-[44px] cursor-pointer"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Confirm Work Done & Release Payment</span>
              </button>
            )}

            {/* If Customer Owner & Job Completed -> Allow Rating */}
            {isOwner && isCompleted && (
              <button
                type="button"
                onClick={() => setIsRatingModalOpen(true)}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-xs shadow-xs flex items-center justify-center gap-1.5 transition-colors min-h-[44px] cursor-pointer"
              >
                <Star className="w-4 h-4 fill-white" />
                <span>Rate Artisan ⭐</span>
              </button>
            )}

            {/* If Customer Owner & Quotes view */}
            {isOwner && !isProCompleted && !isCompleted && !isCancelled && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigateTo('quote_comparison', { jobId: job.id });
                }}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-xs flex items-center justify-center gap-1.5 transition-colors min-h-[44px] cursor-pointer"
              >
                <span>View & Compare Quotes ({jobQuotes.length})</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            )}

            {/* Customer / Admin Report No-Show or Reassign Button when professional is assigned */}
            {(isOwner || isAdmin) && hasAssignedPro && !isCompleted && !isCancelled && !isDisputed && (
              <>
                {!isNoShowReported ? (
                  <button
                    type="button"
                    onClick={() => setIsNoShowModalOpen(true)}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors min-h-[44px] cursor-pointer"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                    <span>Report No-Show</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleOpenReassignModal}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-xs flex items-center justify-center gap-1.5 transition-colors min-h-[44px] cursor-pointer"
                  >
                    <span>Reassign Artisan</span>
                  </button>
                )}
              </>
            )}

            {/* Customer / Admin Cancel Job Button */}
            {(isOwner || isAdmin) && !isCompleted && !isCancelled && !isDisputed && (
              <button
                type="button"
                onClick={() => setIsCancelModalOpen(true)}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors min-h-[44px] cursor-pointer"
              >
                <span>Cancel Job</span>
              </button>
            )}

            {/* Mutual Rescheduling Button */}
            {(isOwner || isAssignedToMe || isAdmin) && hasAssignedPro && !isCompleted && !isCancelled && (
              <button
                type="button"
                onClick={() => setIsRescheduleModalOpen(true)}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-300 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors min-h-[44px] cursor-pointer"
              >
                <Calendar className="w-3.5 h-3.5 text-slate-500" />
                <span>Reschedule</span>
              </button>
            )}

            {/* If Assigned Professional & Job In Progress */}
            {isAssignedToMe && isInProgress && (
              <>
                {!job.professional_arrived && !job.professional_arrived_at && (
                  <button
                    type="button"
                    onClick={handleMarkArrived}
                    disabled={isMarkingArrived}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-md flex items-center justify-center gap-1.5 transition-colors min-h-[44px] cursor-pointer"
                  >
                    <MapPin className="w-4 h-4" />
                    <span>{isMarkingArrived ? 'Marking...' : 'I Have Arrived'}</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setIsDelayModalOpen(true)}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors min-h-[44px] cursor-pointer"
                >
                  <Clock className="w-3.5 h-3.5 text-amber-600" />
                  <span>Report Delay</span>
                </button>

                <button
                  type="button"
                  onClick={() => setIsCompletionModalOpen(true)}
                  className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-md flex items-center justify-center gap-2 transition-colors min-h-[44px] cursor-pointer"
                >
                  <Camera className="w-4 h-4" />
                  <span>Upload Completion Photo</span>
                </button>
              </>
            )}

            {/* If Professional has already submitted a quote for this job */}
            {isProfessional && !isOwner && myQuote && job.status === 'open' && (
              <span className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 font-extrabold text-xs min-h-[44px]">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>Quote Already Sent (₦{myQuote.totalAmount.toLocaleString()})</span>
              </span>
            )}

            {/* If Professional Not yet submitted quote (including direct customer request to this artisan) */}
            {isProfessional && !isOwner && !myQuote && job.status === 'open' && (!job.selectedProfessionalId || job.selectedProfessionalId === currentUser?.id) && (
              <button
                type="button"
                onClick={() => {
                  if (onOpenSubmitQuote) {
                    onClose();
                    onOpenSubmitQuote(job);
                  } else {
                    setIsQuoteModalOpen(true);
                  }
                }}
                className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md flex items-center justify-center gap-1.5 transition-colors min-h-[44px] cursor-pointer"
              >
                <Send className="w-4 h-4" />
                <span>Submit Quote for this Job</span>
              </button>
            )}

            {isProfessional && !isOwner && !isAssignedToMe && !myQuote && (job.status !== 'open' || Boolean(job.selectedProfessionalId)) && (
              <span className="px-3.5 py-2 rounded-xl bg-slate-100 text-slate-500 font-semibold text-xs inline-block">
                Job Assigned / No Longer Open for Quotes
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Customer Confirmation Dialog */}
      {isConfirmDoneDialogOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-100 space-y-4">
            <div className="flex items-center gap-2 text-emerald-800 font-black text-base">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span>Confirm Work Done</span>
            </div>
            <p className="text-xs text-slate-700 leading-relaxed font-medium">
              Please confirm that the professional has completed the job. Once you confirm, the professional's payment will be released to their available balance.
            </p>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Review Notes (Optional)
              </label>
              <textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="e.g., Work completed cleanly and verified."
                rows={2}
                className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 resize-none focus:outline-hidden focus:border-emerald-500"
              />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsConfirmDoneDialogOpen(false)}
                disabled={isConfirmingCompletion}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer min-h-[44px]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmWorkDone}
                disabled={isConfirmingCompletion}
                className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs shadow-md cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-60 min-h-[44px]"
              >
                {isConfirmingCompletion ? 'Releasing...' : 'Confirm Work Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Professional Mark Job Completed Modal */}
      {isCompletionModalOpen && (
        <CompletionPhotoUploadModal
          job={job}
          isOpen={isCompletionModalOpen}
          professionalId={currentUser?.id || ''}
          onClose={() => setIsCompletionModalOpen(false)}
          onSuccess={async () => {
            setIsCompletionModalOpen(false);
            setJob((prev) => ({
              ...prev,
              status: 'completed_by_professional',
              completion_status: 'completed_by_professional',
              completed_by_professional: true,
              professional_completed_at: new Date().toISOString(),
            }));
            if (refreshJobsAndQuotes) {
              await refreshJobsAndQuotes();
            }
          }}
        />
      )}

      {/* Media Zoom Lightbox */}
      {selectedMediaIndex !== null && photos[selectedMediaIndex] && (
        <div
          className="fixed inset-0 z-60 bg-black/90 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setSelectedMediaIndex(null)}
        >
          <button
            onClick={() => setSelectedMediaIndex(null)}
            className="absolute top-6 right-6 p-2 rounded-full bg-white/20 text-white hover:bg-white/40 cursor-pointer"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={photos[selectedMediaIndex]}
            alt="Enlarged job site photo"
            className="max-w-full max-h-[85vh] object-contain rounded-2xl"
          />
        </div>
      )}

      {/* Submit Quote Modal */}
      {isQuoteModalOpen && (
        <SubmitQuoteModal
          job={job}
          isOpen={isQuoteModalOpen}
          onClose={() => {
            setIsQuoteModalOpen(false);
            onClose();
          }}
        />
      )}

      {/* 1-5 Star Rating Modal */}
      {isRatingModalOpen && (
        <JobRatingModal
          job={job}
          isOpen={isRatingModalOpen}
          onClose={() => setIsRatingModalOpen(false)}
        />
      )}

      {/* Cancel Job Modal */}
      {isCancelModalOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl border border-slate-100 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-slate-900">Cancel Job Request</h3>
              <button
                type="button"
                onClick={() => setIsCancelModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              {hasAssignedPro
                ? 'A professional has already been assigned to this job. If work has not started or the professional failed to show up, your escrow payment will be refunded. If work has already started, your request will be routed to Admin Dispute Review.'
                : 'Cancelling this open job will close it for new quotes and notify any professionals who submitted quotes.'}
            </p>
            {hasAssignedPro && !isNoShowReported && (
              <div className="p-3 rounded-2xl bg-slate-50 border border-slate-200 space-y-2 text-xs">
                <span className="font-bold text-slate-800 block">Has the professional already started physical work?</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!cancelWorkStarted}
                    onChange={() => setCancelWorkStarted(false)}
                  />
                  <span>No — work has not started (Eligible for full escrow refund)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={cancelWorkStarted}
                    onChange={() => setCancelWorkStarted(true)}
                  />
                  <span>Yes — work started, but there is an issue (Route to Dispute Review)</span>
                </label>
              </div>
            )}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Reason for Cancellation {hasAssignedPro ? '*' : '(Optional)'}
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                placeholder="Please state why you are cancelling this job..."
                className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 resize-none focus:outline-hidden focus:border-rose-500"
              />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsCancelModalOpen(false)}
                disabled={isCancellingJob}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer"
              >
                Keep Job
              </button>
              <button
                type="button"
                onClick={handleCancelJobSubmit}
                disabled={isCancellingJob}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-xs cursor-pointer disabled:opacity-50"
              >
                {isCancellingJob ? 'Processing...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Professional No-Show Modal */}
      {isNoShowModalOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl border border-slate-100 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-slate-900">Report Professional No-Show</h3>
              <button
                type="button"
                onClick={() => setIsNoShowModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Reporting a no-show immediately freezes payout to the unresponsive artisan and unlocks two options: <strong>Reassign to Another Verified Artisan</strong> (using your existing escrow deposit without paying again) or <strong>Cancel for a Full Refund</strong>.
            </p>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                No-Show Details *
              </label>
              <textarea
                value={noShowReason}
                onChange={(e) => setNoShowReason(e.target.value)}
                rows={3}
                placeholder="e.g., Professional did not arrive at the agreed time and has not responded to calls/messages."
                className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 resize-none focus:outline-hidden focus:border-amber-500"
              />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsNoShowModalOpen(false)}
                disabled={isReportingNoShow}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleReportNoShowSubmit}
                disabled={isReportingNoShow}
                className="flex-1 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-black text-xs cursor-pointer disabled:opacity-50"
              >
                {isReportingNoShow ? 'Reporting...' : 'Report No-Show'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Job Modal */}
      {isReassignModalOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-lg w-full shadow-2xl border border-slate-100 space-y-4 max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-slate-900">Reassign Job to Verified Artisan</h3>
              <button
                type="button"
                onClick={() => setIsReassignModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                ✕
              </button>
            </div>
            <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-950">
              <strong>🔒 Escrow Transfer Protected:</strong> Your existing escrow payment remains valid and will be transferred to the replacement professional. You will <strong>not</strong> be charged again.
            </div>
            {loadingCandidates ? (
              <p className="text-xs text-slate-500 py-4 text-center">Finding verified artisans in {job.lga}, {job.state}...</p>
            ) : reassignmentCandidates.length === 0 ? (
              <p className="text-xs text-slate-600 py-3">
                No replacement professionals are immediately available in this category. You can cancel the job for a full escrow refund.
              </p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {reassignmentCandidates.map((cand) => (
                  <label
                    key={cand.id}
                    className={`flex items-center justify-between p-3 rounded-2xl border text-xs cursor-pointer transition-all ${
                      selectedReplacementProId === cand.id
                        ? 'bg-emerald-50/70 border-emerald-500'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <input
                        type="radio"
                        name="replacementPro"
                        checked={selectedReplacementProId === cand.id}
                        onChange={() => setSelectedReplacementProId(cand.id)}
                      />
                      <div>
                        <span className="font-black text-slate-900 block">{cand.fullName}</span>
                        <span className="text-[11px] text-slate-500">
                          {cand.lga}, {cand.state} • ⭐ {cand.rating.toFixed(1)} ({cand.jobsCompleted} jobs)
                        </span>
                      </div>
                    </div>
                    {cand.existingQuoteAmount && (
                      <span className="px-2 py-0.5 rounded-lg bg-emerald-100 text-emerald-900 font-bold text-[10px]">
                        Quoted ₦{cand.existingQuoteAmount.toLocaleString()}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Reassignment Note (Optional)
              </label>
              <input
                type="text"
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                placeholder="e.g., Reassigned after previous artisan failed to show up"
                className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 focus:outline-hidden focus:border-emerald-500"
              />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsReassignModalOpen(false)}
                disabled={isReassigningJob}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReassignSubmit}
                disabled={isReassigningJob || !selectedReplacementProId}
                className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs cursor-pointer disabled:opacity-50"
              >
                {isReassigningJob ? 'Reassigning...' : 'Confirm Reassignment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Transit Delay Modal */}
      {isDelayModalOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl border border-slate-100 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <span>Report Transit Delay</span>
              </h3>
              <button
                type="button"
                onClick={() => setIsDelayModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Reporting a delay notifies your customer immediately and automatically extends the arrival grace period to protect your reliability rating.
            </p>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Estimated Delay Duration
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[15, 30, 45, 60].map((mins) => (
                  <button
                    key={mins}
                    type="button"
                    onClick={() => setDelayMinutes(mins)}
                    className={`py-2 rounded-xl text-xs font-bold border transition-all ${
                      delayMinutes === mins
                        ? 'bg-amber-500 text-white border-amber-500 shadow-xs'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    +{mins}m
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Reason / Traffic Note
              </label>
              <input
                type="text"
                value={delayReason}
                onChange={(e) => setDelayReason(e.target.value)}
                placeholder="e.g., Heavy traffic along Third Mainland Bridge"
                className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 focus:outline-hidden focus:border-amber-500"
              />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsDelayModalOpen(false)}
                disabled={isReportingDelay}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReportDelaySubmit}
                disabled={isReportingDelay}
                className="flex-1 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-black text-xs cursor-pointer disabled:opacity-50"
              >
                {isReportingDelay ? 'Notifying...' : `Confirm +${delayMinutes}m Delay`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Appointment Modal */}
      {isRescheduleModalOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl border border-slate-100 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                <span>Reschedule Appointment</span>
              </h3>
              <button
                type="button"
                onClick={() => setIsRescheduleModalOpen(false)}
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Rescheduling resets any pending arrival countdowns and updates the appointment time for both the customer and the artisan.
            </p>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                New Appointment Date & Time *
              </label>
              <input
                type="datetime-local"
                value={rescheduleTime}
                onChange={(e) => setRescheduleTime(e.target.value)}
                min={new Date(Date.now() + 15 * 60000).toISOString().slice(0, 16)}
                className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 focus:outline-hidden focus:border-emerald-500 font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Reason for Rescheduling
              </label>
              <input
                type="text"
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                placeholder="e.g., Customer requested morning time instead"
                className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 focus:outline-hidden focus:border-emerald-500"
              />
            </div>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsRescheduleModalOpen(false)}
                disabled={isRescheduling}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRescheduleSubmit}
                disabled={isRescheduling || !rescheduleTime}
                className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs cursor-pointer disabled:opacity-50"
              >
                {isRescheduling ? 'Rescheduling...' : 'Confirm New Time'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
